import React, { useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import {
  FileQuestionIcons,
  LayoutGridIcons,
  TableIcons,
} from '../../../../../../icons/icons';
import {
  Button,
  DataTable,
  FilterSearch,
  MainLayout,
} from '../../../../../../ui';
import { ResultsGridView } from './Components';
import { GenerateReport } from './Containers/GenerateReport';
import UserDetails from './Containers/UserDetails';
import { getFilterLabels, getGridColumns, getTableColumns } from './Functions';
import { IInterviewResult, useResults } from './Hooks';

export interface IResultsProps {
  interviewRoundId: string;
}

export const Results: React.FC<IResultsProps> = ({ interviewRoundId }) => {
  const { t } = useTranslation(NS.adminPages);
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;
  const {
    results,
    pageNumber,
    totalPages,
    rowsPerPage,
    onPageChange,
    handleRowClick,
    handleSelectRow,
    handleSelectAll,
    isOpenUserDetails,
    setIsOpenUserDetails,
    selectedResult,
    sortConfig,
    handleSortChange,
    totalRecords,
    isLoading,
    isRowSelected,
    isAllSelected,
    isIndeterminate,
    selectedCandidateIds,
  } = useResults({ interviewRoundId });

  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [isReportGenerate, setIsReportGenerate] = useState<boolean>(false);

  const listColumns = getTableColumns({ t });
  const gridColumns = getGridColumns({ t });
  const legendItems = [
    { label: t(SI.results.legend.excellent), color: '#22c55e' },
    { label: t(SI.results.legend.good), color: '#3b82f6' },
    { label: t(SI.results.legend.average), color: '#eab308' },
    { label: t(SI.results.legend.notSuitable), color: '#ef4444' },
  ];

  const disabled = selectedCandidateIds.length === 0;

  return (
    <>
      <MainLayout>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, overflow: 'hidden', minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 1,
              alignItems: { lg: 'center' },
              justifyContent: { lg: 'space-between' },
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <FilterSearch
                filterOptions={getFilterLabels()}
                onSearch={() => {}}
                onApplyFilters={() => {}}
              />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, justifyContent: { lg: 'flex-end' } }}>
              {/* Legend Tooltip */}
              <Tooltip
                title={
                  <Stack spacing={1} sx={{ p: 0.5 }}>
                    {legendItems.map(({ label, color }) => (
                      <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: color,
                          }}
                        />
                        <Typography variant='caption'>{label}</Typography>
                      </Box>
                    ))}
                  </Stack>
                }
                arrow
              >
                <IconButton
                  size='small'
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    borderRadius: 1.5,
                    p: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <FileQuestionIcons size={16} />
                </IconButton>
              </Tooltip>

              {/* View toggles & actions */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button
                  type='button'
                  variant='primary'
                  onClick={() => {
                    setIsReportGenerate(true);
                  }}
                  label={t(SI.results.generateReport)}
                  disabled={disabled}
                />
                <Button
                  type='button'
                  variant={viewMode === 'table' ? 'primary' : 'secondary'}
                  onClick={() => setViewMode('table')}
                  icon={<TableIcons size={20} />}
                  hasIcon
                  label=''
                />
                <Button
                  type='button'
                  variant={viewMode === 'grid' ? 'primary' : 'secondary'}
                  onClick={() => setViewMode('grid')}
                  icon={<LayoutGridIcons size={20} />}
                  hasIcon
                  label=''
                />
                <GenerateReport
                  isOpen={isReportGenerate}
                  userIds={selectedCandidateIds}
                  interviewRoundId={interviewRoundId}
                  onCloseDrawer={() => setIsReportGenerate(false)}
                  loading={loading}
                  setLoading={setLoading}
                />
              </Box>
            </Box>
          </Box>

          {viewMode === 'table' ? (
            <DataTable<IInterviewResult>
              onClickRow={handleRowClick}
              onSortChange={handleSortChange}
              sortConfig={sortConfig}
              hasCheckBox
              onSelectAll={handleSelectAll}
              onSelectRow={handleSelectRow}
              isRowSelected={isRowSelected}
              isAllSelected={isAllSelected}
              isIndeterminate={isIndeterminate}
              columns={listColumns}
              data={results}
              rowsPerPage={10}
              currentPage={pageNumber}
              totalPages={totalPages}
              onPageChange={onPageChange}
              totalRecords={totalRecords}
              isLoading={isLoading}
            />
          ) : (
            <ResultsGridView<IInterviewResult>
              onSortChange={handleSortChange}
              sortConfig={sortConfig}
              rowHeaders={gridColumns}
              data={results}
              columns={gridColumns}
              onItemClick={handleRowClick}
              page={pageNumber}
              totalPages={totalPages}
              onPageChange={onPageChange}
              rowsPerPage={rowsPerPage}
              currentPage={pageNumber}
              totalRecords={totalRecords}
            />
          )}
        </Box>
      </MainLayout>

      <UserDetails
        isOpen={isOpenUserDetails}
        onCloseDrawer={() => setIsOpenUserDetails(false)}
        result={selectedResult}
      />
    </>
  );
};

export default Results;
